import { Button, getTypographyDefaultColor, Modal, Space, Typography } from '@ag1/ui';
import { toPlainText } from '@portabletext/react';
import { useGateValue } from '@statsig/react-bindings';
import { buttonClickEvent, clickPurchaseIntentEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useGoToOffersV2 } from 'shared/hooks/useGoToOffersV2';
import { useLocale } from 'shared/hooks/useLocale';
import IngredientModal from 'shared/modals/IngredientModal/IngredientModal';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { getButtonFileLinkUrl } from 'shared/utils/getButtonFileLinkUrl';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { gladlyShow } from 'shared/utils/gladly/gladly';
import { launchSierra } from 'shared/utils/sierra/sierra';
import { SSGates } from 'state/context/StatsigContext/StatsigFlags';
import { useStoreValue } from 'state/hooks/useStore';
import { ImageAndTextV3, UnifiedButton as UnifiedButtonType } from 'types/studio/studio';
import { useLiveSupportOnline } from 'shared/hooks';
import { LocaleEnum } from 'schema/locale';
import { isSupportOnlineUK } from 'shared/utils/liveSupport/isSupportOnlineUK';
import { SliderButton } from '../../SliderButton/SliderButton';
import styles from './ImageAndTextContentV3.module.scss';
import { UnifiedButton } from '../../UnifiedButton';

type Props = { sectionType: string } & Pick<
  ImageAndTextV3,
  | 'headline'
  | 'headlinePosition'
  | 'buttonCTA'
  | 'text'
  | 'subtitle'
  | 'secondSubtitle'
  | 'theme'
  | 'headlineSize'
  | 'backgroundColor'
>;

const ImageAndTextContentV3 = ({
  sectionType,
  subtitle,
  headline,
  headlineSize = 'h2',
  headlinePosition,
  buttonCTA,
  theme,
  secondSubtitle,
  text,
  backgroundColor
}: Props) => {
  const locale = useLocale();
  const isSierraChat = useGateValue(SSGates.sierraChatRollout);
  const isUkLiveSupportOnline = useLiveSupportOnline(LocaleEnum.enUK, isSupportOnlineUK);

  const gatedText = useMemo(() => {
    if (!isSierraChat || buttonCTA?.cta !== 'chat' || !text) return text;

    const textCopy = text.slice(0);
    const infoBlock = textCopy[1].children;

    // Finds and removes email row
    const emailIndex = infoBlock.findIndex((i: any) => i.text === 'Email');
    if (emailIndex === -1) return text;

    const nextNewlineIndex = infoBlock.findIndex(
      (i: any, idx: number) => idx > emailIndex && i.text === '\n'
    );
    if (nextNewlineIndex === -1) return text;

    const slicedInfoBlock = [
      ...infoBlock.slice(0, emailIndex),
      ...infoBlock.slice(nextNewlineIndex + 1)
    ];

    textCopy[1].children = slicedInfoBlock;
    return textCopy;
  }, [text, buttonCTA, isSierraChat]);

  const buttonLink = useMemo(() => {
    if (buttonCTA?.link?.btnLink?.fileLink?.asset) {
      return getButtonFileLinkUrl(buttonCTA.link?.btnLink?.fileLink);
    }

    return getSanityLinkUrl(buttonCTA?.link?.btnLink, locale);
  }, [buttonCTA?.link, locale]);

  const { goToOffer } = useGoToOffersV2({
    withScroll: false,
    bundleUrl: buttonCTA?.offerButton?.bundleUrl,
    buttonLink: buttonCTA?.offerButton?.buttonLink
  });

  const store = useStoreValue();

  const handleButtonClickOffer = () => {
    const buttonText = buttonCTA?.offerButton?.button?.portableText
      ? toPlainText(buttonCTA.offerButton?.button.portableText)
      : '';
    clickPurchaseIntentEvent({
      store,
      clickText: buttonText,
      location: sectionType,
      url: buttonCTA?.offerButton?.bundleUrl || buttonCTA?.offerButton?.buttonLink
    });
    goToOffer();
  };

  const [isOpen, setOpen] = useState(false);

  const handleButtonClick = () => {
    buttonClickEvent({
      clickText: buttonCTA?.link?.portableText
        ? toPlainText(buttonCTA?.link?.portableText)
        : undefined,
      location: sectionType,
      url: buttonLink
    });
  };

  const getHeadlineSize = () => {
    if (headlineSize === 'h3') return '3';
    return '2';
  };

  const shouldUseUnifiedButton = useMemo(
    () =>
      !!buttonCTA?.unifiedButton?.switchButton &&
      buttonCTA.unifiedButton.switchButton !== 'noButton',
    [buttonCTA]
  );

  return (
    <>
      {subtitle && (
        <Typography
          variant="subtitleMono"
          size="2"
          noPadding
          color={backgroundColor === 'green' ? 'brand-white' : getTypographyDefaultColor(theme)}
          className={clsx(
            styles.subtitle,
            styles.supersubtitle,
            headlinePosition === 'above' && styles.aboveLarge
          )}>
          {subtitle}
        </Typography>
      )}
      {headline && (
        <Typography
          variant="headlineSans"
          size={getHeadlineSize()}
          component={headlineSize}
          noPadding
          color={backgroundColor === 'green' ? 'brand-white' : getTypographyDefaultColor(theme)}
          className={clsx(styles.headline, headlinePosition === 'above' && styles.aboveLarge)}>
          {headline}
        </Typography>
      )}
      {secondSubtitle && (
        <Typography
          variant="body"
          size="1"
          component="div"
          noPadding
          color={backgroundColor === 'green' ? 'brand-white' : getTypographyDefaultColor(theme)}
          className={clsx(
            styles.secondSubtitle,
            headlinePosition === 'above' && styles.headlineAbove
          )}>
          <CustomPortableText value={secondSubtitle} />
        </Typography>
      )}
      {gatedText && (
        <CustomPortableText
          addAccessibility
          value={gatedText}
          className={clsx(
            styles.text,
            headlinePosition === 'above' && !secondSubtitle && styles.noMargin,
            getThemeCSSClass(theme)
          )}
        />
      )}

      {shouldUseUnifiedButton ? (
        <UnifiedButton
          unifiedButton={buttonCTA?.unifiedButton as UnifiedButtonType}
          sectionType={sectionType}
          size="medium"
          theme={theme}
          className={styles.button}
        />
      ) : (
        <>
          {buttonCTA?.link && buttonCTA?.cta === 'link' && (
            <Button
              size="medium"
              rounded
              fluid
              variant={buttonCTA?.link?.variant}
              target={buttonCTA?.link?.targetBlank ? '_blank' : '_self'}
              href={buttonLink}
              theme={theme}
              data-gtm-skip-global
              onClick={handleButtonClick}
              icon={buttonCTA?.link?.showIcon && buttonCTA?.link?.iconOptions?.icon}
              iconPosition={buttonCTA?.link?.iconOptions?.position}
              className={clsx(styles.button, buttonCTA?.link?.variant === 'link' && styles.left)}>
              {buttonCTA?.link?.portableText && (
                <CustomPortableText value={buttonCTA?.link?.portableText} />
              )}
            </Button>
          )}
          {isUkLiveSupportOnline && buttonCTA?.chatButton && buttonCTA?.cta === 'chat' && (
            <Button
              size="medium"
              rounded
              fluid
              theme={theme}
              variant={buttonCTA?.chatButton?.variant}
              data-gtm-skip-global
              icon={buttonCTA?.chatButton?.showIcon && buttonCTA?.chatButton?.iconOptions?.icon}
              iconPosition={buttonCTA?.chatButton?.iconOptions?.position}
              className={styles.button}
              onClick={isSierraChat ? launchSierra : gladlyShow}>
              {buttonCTA?.chatButton?.portableText &&
                (isSierraChat ? 'Start Chat' : toPlainText(buttonCTA?.chatButton?.portableText))}
            </Button>
          )}
          {buttonCTA?.anchorButton && buttonCTA?.cta === 'anchor' && (
            <Button
              size="medium"
              rounded
              fluid
              variant={buttonCTA?.anchorButton?.variant}
              href={`#${buttonCTA?.anchorButtonLink}`}
              data-gtm-skip-global
              theme={theme}
              icon={buttonCTA?.anchorButton?.showIcon && buttonCTA?.anchorButton?.iconOptions?.icon}
              iconPosition={buttonCTA?.anchorButton?.iconOptions?.position}
              className={styles.button}>
              {buttonCTA?.anchorButton?.portableText && (
                <CustomPortableText value={buttonCTA?.anchorButton?.portableText} />
              )}
            </Button>
          )}
          {buttonCTA?.cta === 'modal' && buttonCTA?.modalButton && (
            <Button
              size="medium"
              rounded
              fluid
              variant={buttonCTA?.modalButton?.variant}
              target="_self"
              theme={theme}
              data-gtm-skip-global
              icon={buttonCTA?.modalButton?.showIcon && buttonCTA?.modalButton?.iconOptions?.icon}
              iconPosition={buttonCTA?.modalButton?.iconOptions?.position}
              className={styles.button}
              onClick={() => setOpen(true)}>
              {buttonCTA?.modalButton?.portableText && (
                <CustomPortableText value={buttonCTA?.modalButton?.portableText} />
              )}
            </Button>
          )}
          {buttonCTA?.cta === 'slider' && (
            <Space top={{ xxs: 24, m: 32 }} bottom={{ m: 60 }}>
              <div className={styles.sliderButtons}>
                {buttonCTA?.sliderButtonItems?.map((item) => (
                  <SliderButton
                    key={item._key}
                    sliderButtonItem={item}
                    sliderButtonItems={buttonCTA?.sliderButtonItems}
                    hasSliderArrows={buttonCTA?.hasSliderArrows}
                    hasSliderDividers={buttonCTA?.hasSliderDividers}
                    sliderButtonVariant={buttonCTA?.sliderButtonVariant}
                    useRegularButton={buttonCTA?.useRegularButton}
                  />
                ))}
              </div>
            </Space>
          )}
          {buttonCTA?.offerButton && buttonCTA?.cta === 'buildABundle' && (
            <Button
              size="medium"
              variant={buttonCTA.offerButton?.button?.variant}
              fluid
              rounded
              target={buttonCTA?.offerButton?.button?.targetBlank ? '_blank' : '_self'}
              icon={
                buttonCTA?.offerButton?.button?.showIcon &&
                buttonCTA?.offerButton?.button?.iconOptions?.icon
              }
              theme={theme}
              iconPosition={buttonCTA?.offerButton?.button?.iconOptions?.position}
              className={styles.button}
              data-gtm-skip-global
              onClick={handleButtonClickOffer}>
              {buttonCTA?.offerButton?.button?.portableText && (
                <CustomPortableText value={buttonCTA.offerButton?.button.portableText} />
              )}
            </Button>
          )}
        </>
      )}
      <Modal
        isOpen={isOpen && !shouldUseUnifiedButton}
        onClose={() => setOpen(false)}
        mode="fullScreen">
        <div className={styles.modalInner}>
          {!shouldUseUnifiedButton && (
            <IngredientModal isOpen={isOpen} onClose={() => setOpen(false)} theme={theme} />
          )}
        </div>
      </Modal>
    </>
  );
};

export default ImageAndTextContentV3;
